import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Mail, Building2, CalendarDays, KeyRound, LogIn, UserPlus, Lock, RotateCcw, Dices,
  ClipboardCheck, MessagesSquare, Activity, ShieldQuestion, ShieldAlert,
} from "lucide-react";

const TYPE_META = {
  signup: { icon: UserPlus, color: "text-emerald-600 bg-emerald-50 border-emerald-200" },
  login: { icon: LogIn, color: "text-blue-600 bg-blue-50 border-blue-200" },
  quiz_attempt: { icon: ClipboardCheck, color: "text-[#E53935] bg-red-50 border-red-200" },
  simulator: { icon: MessagesSquare, color: "text-violet-600 bg-violet-50 border-violet-200" },
  account_locked: { icon: ShieldAlert, color: "text-amber-700 bg-amber-50 border-amber-300" },
  password_reset: { icon: RotateCcw, color: "text-slate-600 bg-slate-50 border-slate-300" },
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

const Stat = ({ label, value, testId }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5 text-center" data-testid={testId}>
    <div className="text-lg font-bold text-slate-900 leading-tight">{value ?? "—"}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>
  </div>
);

export const LearnerActivityDialog = ({ learner, open, onOpenChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = () => {
    if (!learner?.id) return;
    setLoading(true);
    setData(null);
    api.get(`/learners/${learner.id}/activity`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) { setNewPassword(""); load(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, learner?.id]);

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const arr = new Uint32Array(10);
    window.crypto.getRandomValues(arr);
    setNewPassword(Array.from(arr, (n) => chars[n % chars.length]).join(""));
  };

  const resetPassword = async () => {
    if (newPassword.length < 8) return toast.error("Temporary password must be at least 8 characters");
    setResetting(true);
    try {
      await api.post(`/learners/${learner.id}/reset-password`, { new_password: newPassword });
      toast.success("Password reset & account unlocked — share the new password with the learner");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const acc = data?.learner;
  const stats = data?.stats;
  const timeline = data?.timeline || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="learner-activity-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity size={18} className="text-[#E53935]" />
            {learner?.name || "Learner"} — account & activity
          </DialogTitle>
          <DialogDescription className="sr-only">Account details, password controls and full activity timeline for this learner.</DialogDescription>
        </DialogHeader>

        {loading && <div className="py-10 text-center text-sm text-slate-500">Loading activity…</div>}

        {!loading && acc && (
          <div className="space-y-5">
            {/* Account card */}
            <div className="border border-slate-200 rounded-xl p-4 space-y-2" data-testid="learner-account-card">
              <div className="flex flex-wrap gap-x-6 gap-y-1.5 text-sm text-slate-700">
                <span className="inline-flex items-center gap-1.5"><Mail size={13} className="text-slate-400" /> {acc.email}</span>
                <span className="inline-flex items-center gap-1.5"><Building2 size={13} className="text-slate-400" /> {acc.department || "No department"}</span>
                <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} className="text-slate-400" /> Joined {fmt(acc.added_at)}</span>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                {acc.locked && (
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-100" data-testid="locked-badge">
                    <Lock size={11} className="mr-1" /> Locked ({acc.failed_login_attempts} failed attempts)
                  </Badge>
                )}
                <Badge variant="outline" className={acc.has_password ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}>
                  <KeyRound size={11} className="mr-1" />
                  {acc.has_password ? "Password set" : "No password yet"}
                </Badge>
                <Badge variant="outline" className="border-slate-300 text-slate-600">
                  Last sign-in: {fmt(acc.last_login_at)}
                </Badge>
              </div>
            </div>

            {/* Admin password reset (also unlocks the account) */}
            <div className={`border rounded-xl p-4 ${acc.locked ? "border-amber-300 bg-amber-50/50" : "border-slate-200"}`} data-testid="reset-password-section">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2 flex items-center gap-1.5">
                <RotateCcw size={12} /> Reset password {acc.locked && <span className="text-amber-700 normal-case font-medium">— this will unlock the account</span>}
              </div>
              <div className="flex gap-2">
                <Input
                  data-testid="new-password-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New temporary password (min. 8 chars)"
                  className="bg-white font-mono text-sm"
                />
                <Button type="button" variant="outline" onClick={generatePassword} data-testid="generate-password-button" title="Generate a random password">
                  <Dices size={14} />
                </Button>
                <Button
                  type="button"
                  onClick={resetPassword}
                  disabled={resetting || newPassword.length < 8}
                  data-testid="confirm-reset-password-button"
                  className="bg-[#222222] hover:bg-black text-white"
                >
                  {resetting ? "Resetting…" : "Reset"}
                </Button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1.5">Passwords are stored encrypted and can never be viewed — set a new one and share it with the learner directly.</p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-5 gap-2">
              <Stat label="Sign-ins" value={stats.logins} testId="stat-logins" />
              <Stat label="Quiz tries" value={stats.quiz_attempts} testId="stat-attempts" />
              <Stat label="Passed" value={stats.modules_passed} testId="stat-passed" />
              <Stat label="Avg score" value={stats.avg_percentage != null ? `${stats.avg_percentage}%` : "—"} testId="stat-avg" />
              <Stat label="Sim sessions" value={stats.simulator_sessions} testId="stat-sims" />
            </div>

            {/* Timeline */}
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">Activity timeline</div>
              {timeline.length === 0 ? (
                <div className="text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg p-6 text-center" data-testid="empty-timeline">
                  <ShieldQuestion size={20} className="mx-auto mb-1.5 text-slate-300" />
                  No activity recorded yet.
                </div>
              ) : (
                <ol className="space-y-2" data-testid="activity-timeline">
                  {timeline.map((ev, i) => {
                    const meta = TYPE_META[ev.type] || TYPE_META.login;
                    const Icon = meta.icon;
                    return (
                      <li key={`${ev.type}-${ev.at}-${i}`} className="flex items-start gap-3 border border-slate-100 rounded-lg px-3 py-2.5">
                        <span className={`shrink-0 w-7 h-7 rounded-full border grid place-items-center ${meta.color}`}>
                          <Icon size={13} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-slate-800 flex items-center gap-2 flex-wrap">
                            {ev.title}
                            {ev.type === "quiz_attempt" && ev.percentage != null && (
                              <Badge variant="outline" className={ev.passed ? "border-emerald-300 text-emerald-700" : "border-red-200 text-red-600"}>
                                {Math.round(ev.percentage)}%
                              </Badge>
                            )}
                          </div>
                          {ev.detail && <div className="text-xs text-slate-500 mt-0.5">{ev.detail}</div>}
                        </div>
                        <div className="text-[11px] text-slate-400 whitespace-nowrap shrink-0">{fmt(ev.at)}</div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          </div>
        )}

        {!loading && !acc && <div className="py-8 text-center text-sm text-slate-500">Could not load activity.</div>}
      </DialogContent>
    </Dialog>
  );
};
